import { Tool } from '@modelcontextprotocol/sdk/types.js';
import {
  AnalyzeTestCoverageInputSchema,
  type Config,
  type TestCoverageResult,
  type TestCoverageCategory,
} from '../types/index.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import { findFiles } from '../utils/fs.js';
import path from 'path';

export const analyzeTestCoverageTool: Tool = {
  name: 'analyze_test_coverage',
  description: 'Analyze test coverage for a SmartStack project. Identifies entities, services, and controllers without tests, calculates coverage ratios, and provides recommendations.',
  inputSchema: {
    type: 'object',
    properties: {
      path: {
        type: 'string',
        description: 'Project path to analyze (default: configured SmartStack path)',
      },
      scope: {
        type: 'string',
        enum: ['entity', 'service', 'controller', 'all'],
        default: 'all',
        description: 'Scope of analysis',
      },
      outputFormat: {
        type: 'string',
        enum: ['summary', 'detailed', 'json'],
        default: 'summary',
        description: 'Output format',
      },
      includeRecommendations: {
        type: 'boolean',
        default: true,
        description: 'Include recommendations for missing tests',
      },
    },
  },
};

export async function handleAnalyzeTestCoverage(
  args: unknown,
  config: Config
): Promise<string> {
  const input = AnalyzeTestCoverageInputSchema.parse(args);
  const projectPath = input.path || config.smartstack.projectPath;

  logger.info('Analyzing test coverage', { projectPath, scope: input.scope });

  const structure = await findSmartStackStructure(projectPath);

  const result: TestCoverageResult = {
    summary: { total: 0, tested: 0, coverage: 0 },
    entities: { total: 0, tested: 0, coverage: 0, items: [], missingTests: [] },
    services: { total: 0, tested: 0, coverage: 0, items: [], missingTests: [] },
    controllers: { total: 0, tested: 0, coverage: 0, items: [], missingTests: [] },
    missing: [],
    recommendations: [],
  };

  try {
    if (input.scope === 'all' || input.scope === 'entity') {
      await analyzeEntityCoverage(structure, result);
    }

    if (input.scope === 'all' || input.scope === 'service') {
      await analyzeServiceCoverage(structure, result);
    }

    if (input.scope === 'all' || input.scope === 'controller') {
      await analyzeControllerCoverage(structure, result);
    }

    // Calculate overall summary
    result.summary.total = result.entities.total + result.services.total + result.controllers.total;
    result.summary.tested = result.entities.tested + result.services.tested + result.controllers.tested;
    result.summary.coverage = result.summary.total > 0
      ? Math.round((result.summary.tested / result.summary.total) * 100)
      : 0;

    // Generate recommendations
    if (input.includeRecommendations) {
      generateRecommendations(result);
    }

  } catch (error) {
    logger.error('Error analyzing test coverage', error);
    return `Error: ${error instanceof Error ? error.message : String(error)}`;
  }

  return formatCoverageResult(result, input.outputFormat);
}

// ============================================================================
// Analysis Functions
// ============================================================================

async function analyzeEntityCoverage(
  structure: { root: string; domain?: string; application?: string; infrastructure?: string; api?: string },
  result: TestCoverageResult
): Promise<void> {
  if (!structure.domain) {
    result.recommendations.push('Domain layer not found - cannot analyze entity coverage');
    return;
  }

  // Find all entity files in Domain
  const entityFiles = await findFiles('**/*.cs', { cwd: structure.domain });
  const entityNames = extractComponentNames(entityFiles, ['Entity', 'Aggregate']);

  // Find all entity tests
  const testPath = path.join(structure.root, 'Tests', 'Unit', 'Domain');
  let testFiles: string[] = [];
  try {
    testFiles = await findFiles('**/*Tests.cs', { cwd: testPath });
  } catch {
    // Tests directory might not exist
  }
  const testedEntities = extractTestedNames(testFiles);

  result.entities.total = entityNames.length;
  result.entities.items = entityNames;
  result.entities.tested = entityNames.filter(e => testedEntities.includes(e)).length;
  result.entities.coverage = result.entities.total > 0
    ? Math.round((result.entities.tested / result.entities.total) * 100)
    : 0;
  result.entities.missingTests = entityNames.filter(e => !testedEntities.includes(e));

  // Add to missing items with priority
  for (const name of result.entities.missingTests) {
    result.missing.push({
      name,
      type: 'entity',
      priority: determinePriority(name, 'entity'),
      recommendation: `Create unit tests for ${name} entity (factory methods, soft delete, audit trail)`,
      suggestedTestFile: `Tests/Unit/Domain/${name}Tests.cs`,
    });
  }
}

async function analyzeServiceCoverage(
  structure: { root: string; domain?: string; application?: string; infrastructure?: string; api?: string },
  result: TestCoverageResult
): Promise<void> {
  if (!structure.application) {
    result.recommendations.push('Application layer not found - cannot analyze service coverage');
    return;
  }

  // Find all service interfaces
  const serviceFiles = await findFiles('**/I*Service.cs', { cwd: structure.application });
  const serviceNames = serviceFiles.map(f => {
    const basename = path.basename(f, '.cs');
    // Remove 'I' prefix to get service name
    return basename.startsWith('I') ? basename.slice(1) : basename;
  }).filter(n => n.endsWith('Service')).map(n => n.replace(/Service$/, ''));

  // Find all service tests
  const testPath = path.join(structure.root, 'Tests', 'Unit', 'Services');
  let testFiles: string[] = [];
  try {
    testFiles = await findFiles('**/*ServiceTests.cs', { cwd: testPath });
  } catch {
    // Tests directory might not exist
  }
  const testedServices = testFiles.map(f => {
    const basename = path.basename(f, '.cs');
    return basename.replace(/ServiceTests$/, '');
  });

  result.services.total = serviceNames.length;
  result.services.items = serviceNames;
  result.services.tested = serviceNames.filter(s => testedServices.includes(s)).length;
  result.services.coverage = result.services.total > 0
    ? Math.round((result.services.tested / result.services.total) * 100)
    : 0;
  result.services.missingTests = serviceNames.filter(s => !testedServices.includes(s));

  // Add to missing items with priority
  for (const name of result.services.missingTests) {
    result.missing.push({
      name,
      type: 'service',
      priority: determinePriority(name, 'service'),
      recommendation: `Create unit tests for ${name}Service (CRUD operations, business logic, error handling)`,
      suggestedTestFile: `Tests/Unit/Services/${name}ServiceTests.cs`,
    });
  }
}

async function analyzeControllerCoverage(
  structure: { root: string; domain?: string; application?: string; infrastructure?: string; api?: string },
  result: TestCoverageResult
): Promise<void> {
  if (!structure.api) {
    result.recommendations.push('API layer not found - cannot analyze controller coverage');
    return;
  }

  // Find all controllers
  const controllerFiles = await findFiles('**/*Controller.cs', { cwd: structure.api });
  const controllerNames = controllerFiles.map(f => {
    const basename = path.basename(f, '.cs');
    return basename.replace(/Controller$/, '');
  }).filter(n => n !== 'Base'); // Exclude BaseController

  // Find all controller tests
  const testPath = path.join(structure.root, 'Tests', 'Integration', 'Controllers');
  let testFiles: string[] = [];
  try {
    testFiles = await findFiles('**/*ControllerTests.cs', { cwd: testPath });
  } catch {
    // Tests directory might not exist
  }
  const testedControllers = testFiles.map(f => {
    const basename = path.basename(f, '.cs');
    return basename.replace(/ControllerTests$/, '');
  });

  result.controllers.total = controllerNames.length;
  result.controllers.items = controllerNames;
  result.controllers.tested = controllerNames.filter(c => testedControllers.includes(c)).length;
  result.controllers.coverage = result.controllers.total > 0
    ? Math.round((result.controllers.tested / result.controllers.total) * 100)
    : 0;
  result.controllers.missingTests = controllerNames.filter(c => !testedControllers.includes(c));

  // Add to missing items with priority
  for (const name of result.controllers.missingTests) {
    result.missing.push({
      name,
      type: 'controller',
      priority: determinePriority(name, 'controller'),
      recommendation: `Create integration tests for ${name}Controller (HTTP status codes, authorization, validation)`,
      suggestedTestFile: `Tests/Integration/Controllers/${name}ControllerTests.cs`,
    });
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function extractComponentNames(files: string[], excludeSuffixes: string[] = []): string[] {
  return files
    .map(f => path.basename(f, '.cs'))
    .filter(name => {
      // Exclude common non-entity files
      const excludePatterns = [
        'Configuration', 'Extensions', 'Handler', 'Specification',
        'Repository', 'Service', 'Controller', 'Dto', 'Validator',
        ...excludeSuffixes
      ];
      return !excludePatterns.some(pattern => name.endsWith(pattern));
    })
    .filter(name => !name.startsWith('I')) // Exclude interfaces
    .filter(name => !name.startsWith('Base')); // Exclude base classes
}

function extractTestedNames(testFiles: string[]): string[] {
  return testFiles.map(f => {
    const basename = path.basename(f, '.cs');
    return basename.replace(/Tests$/, '');
  });
}

function determinePriority(name: string, type: 'entity' | 'service' | 'controller'): 'critical' | 'high' | 'medium' | 'low' {
  const criticalPatterns = ['User', 'Auth', 'Payment', 'Order', 'Tenant', 'Security', 'Permission', 'Role'];
  const highPatterns = ['Account', 'Invoice', 'Transaction', 'Session', 'Token', 'Subscription'];

  const nameLower = name.toLowerCase();

  if (criticalPatterns.some(p => nameLower.includes(p.toLowerCase()))) {
    return 'critical';
  }

  if (highPatterns.some(p => nameLower.includes(p.toLowerCase()))) {
    return 'high';
  }

  // Controllers are generally higher priority than entities
  if (type === 'controller') {
    return 'high';
  }

  if (type === 'service') {
    return 'medium';
  }

  return 'medium';
}

function generateRecommendations(result: TestCoverageResult): void {
  // Coverage-based recommendations
  if (result.summary.coverage < 50) {
    result.recommendations.push('CRITICAL: Overall test coverage is below 50%. Prioritize adding tests for critical components.');
  } else if (result.summary.coverage < 80) {
    result.recommendations.push('Test coverage is below recommended 80% threshold. Consider adding more tests.');
  }

  // Category-specific recommendations
  if (result.entities.coverage < 60) {
    result.recommendations.push(`Entity coverage (${result.entities.coverage}%) is low. Focus on testing factory methods, soft delete, and audit trails.`);
  }

  if (result.services.coverage < 70) {
    result.recommendations.push(`Service coverage (${result.services.coverage}%) is low. Ensure CRUD operations and business logic are tested.`);
  }

  if (result.controllers.coverage < 80) {
    result.recommendations.push(`Controller coverage (${result.controllers.coverage}%) is low. Add integration tests for HTTP endpoints.`);
  }

  // Priority-based recommendations
  const criticalMissing = result.missing.filter(m => m.priority === 'critical');
  if (criticalMissing.length > 0) {
    result.recommendations.push(`${criticalMissing.length} CRITICAL component(s) have no tests: ${criticalMissing.map(m => m.name).join(', ')}`);
  }

  // Security recommendations
  const securityComponents = result.missing.filter(m =>
    m.name.toLowerCase().includes('auth') ||
    m.name.toLowerCase().includes('security') ||
    m.name.toLowerCase().includes('permission')
  );
  if (securityComponents.length > 0) {
    result.recommendations.push(`Security-sensitive components without tests: ${securityComponents.map(m => m.name).join(', ')}. Add security tests immediately.`);
  }
}

// ============================================================================
// Formatting
// ============================================================================

function formatCoverageResult(
  result: TestCoverageResult,
  format: string
): string {
  if (format === 'json') {
    return JSON.stringify(result, null, 2);
  }

  const lines: string[] = [];

  lines.push('# Test Coverage Analysis');
  lines.push('');

  // Summary
  const coverageEmoji = result.summary.coverage >= 80 ? '✅' : result.summary.coverage >= 50 ? '⚠️' : '❌';
  lines.push('## Summary');
  lines.push('');
  lines.push(`| Metric | Value |`);
  lines.push(`|--------|-------|`);
  lines.push(`| **Overall Coverage** | ${coverageEmoji} ${result.summary.coverage}% (${result.summary.tested}/${result.summary.total}) |`);
  lines.push(`| Entities | ${formatCoverageCell(result.entities)} |`);
  lines.push(`| Services | ${formatCoverageCell(result.services)} |`);
  lines.push(`| Controllers | ${formatCoverageCell(result.controllers)} |`);
  lines.push('');

  if (format === 'detailed') {
    // Detailed breakdown
    if (result.entities.missingTests.length > 0) {
      lines.push('## Missing Entity Tests');
      lines.push('');
      for (const name of result.entities.missingTests) {
        lines.push(`- [ ] ${name}`);
      }
      lines.push('');
    }

    if (result.services.missingTests.length > 0) {
      lines.push('## Missing Service Tests');
      lines.push('');
      for (const name of result.services.missingTests) {
        lines.push(`- [ ] ${name}Service`);
      }
      lines.push('');
    }

    if (result.controllers.missingTests.length > 0) {
      lines.push('## Missing Controller Tests');
      lines.push('');
      for (const name of result.controllers.missingTests) {
        lines.push(`- [ ] ${name}Controller`);
      }
      lines.push('');
    }
  }

  // Priority list
  if (result.missing.length > 0) {
    lines.push('## Missing Tests (By Priority)');
    lines.push('');

    const priorities: Array<'critical' | 'high' | 'medium' | 'low'> = ['critical', 'high', 'medium', 'low'];
    for (const priority of priorities) {
      const items = result.missing.filter(m => m.priority === priority);
      if (items.length > 0) {
        const emoji = priority === 'critical' ? '🔴' : priority === 'high' ? '🟠' : priority === 'medium' ? '🟡' : '🟢';
        lines.push(`### ${emoji} ${priority.charAt(0).toUpperCase() + priority.slice(1)} Priority`);
        lines.push('');
        lines.push('| Component | Type | Suggested Test File |');
        lines.push('|-----------|------|---------------------|');
        for (const item of items) {
          lines.push(`| ${item.name} | ${item.type} | \`${item.suggestedTestFile}\` |`);
        }
        lines.push('');
      }
    }
  }

  // Recommendations
  if (result.recommendations.length > 0) {
    lines.push('## Recommendations');
    lines.push('');
    for (const rec of result.recommendations) {
      const emoji = rec.includes('CRITICAL') ? '🔴' : rec.includes('low') ? '⚠️' : '💡';
      lines.push(`${emoji} ${rec}`);
      lines.push('');
    }
  }

  // Action items
  lines.push('## Quick Actions');
  lines.push('');
  lines.push('Generate tests for missing components using:');
  lines.push('');
  if (result.missing.length > 0) {
    const firstMissing = result.missing[0];
    lines.push('```');
    lines.push(`scaffold_tests(target: "${firstMissing.type}", name: "${firstMissing.name}")`);
    lines.push('```');
  }
  lines.push('');

  return lines.join('\n');
}

function formatCoverageCell(category: TestCoverageCategory): string {
  const emoji = category.coverage >= 80 ? '✅' : category.coverage >= 50 ? '⚠️' : '❌';
  return `${emoji} ${category.coverage}% (${category.tested}/${category.total})`;
}
